import { format, isAfter } from "date-fns";
import * as _ from "lodash-es";

import type {
  Attendee,
  Controller,
  Document,
  FullEvent,
  ParentId,
  People,
  ReferenceId,
  ReleasableRef,
  Task,
} from "@ctrl/controller-rpc";

import { useObjectTitle } from "@/components/graph/title";
import { releasableRef } from "@/ref";
import type { Router } from "@/types";
import {
  navigateToInternalPathOrObject,
  navigateToObject,
} from "@/utils/navigate";
import { openExternalUrl } from "@/utils/open-external-url";
import { shouldOpenLink } from "@/utils/should-open-link";
import { property } from "@/utils/unknown";

import type { Tooltip } from "./tooltip";

async function asyncFind<T>(
  array: T[],
  asyncPredicate: (item: T) => Promise<boolean>
): Promise<T | undefined> {
  for (const item of array) {
    if (await asyncPredicate(item)) {
      return item;
    }
  }
  return undefined;
}
async function findNextAllocation(
  task: Task,
  controller: Controller
): Promise<ReleasableRef<FullEvent | null>> {
  const now = new Date();
  const allocationId = await asyncFind(task.allocations || [], async (eId) => {
    const event = await controller.event.get(eId);
    const eventValue = event.value;
    event.release();
    if (!eventValue) return false;
    return isAfter(eventValue.start, now);
  });
  if (!allocationId) return releasableRef(null);
  return await controller.event.get(allocationId);
}

const mousedown = async (controller: Controller, e: MouseEvent) => {
  // Prevent the element from gaining focus when opening a link.
  // Don't prevent if ALT is held (for caret positioning) or if it's a regular click (we want to open the link).
  if (shouldOpenLink(await controller.os(), e) && !e.altKey) {
    e.preventDefault();
  }
};
async function addOrRemoveListenerOnLink(
  action: "add" | "remove",
  elm: Element,
  controller: Controller,
  router?: Router | null
) {
  if (!elm) return;
  Array.from(elm.getElementsByTagName("a")).forEach(
    (el: HTMLAnchorElement & { _tippy?: Tooltip | null }) => {
      const remove = () => {
        el._tippy?.destroy();
        el.removeEventListener("mousedown", (e) => mousedown(controller, e));
        el.removeEventListener("click", click);
      };
      const click = async (e: MouseEvent) => {
        // If ALT is held, position the caret instead of opening the link
        if (e.altKey) {
          e.preventDefault();
          e.stopPropagation();
          // Find the parent contenteditable element
          let parent: HTMLElement | null = el.parentElement;
          while (parent && parent.getAttribute("contenteditable") !== "true") {
            parent = parent.parentElement;
          }
          if (parent) {
            // Get the click position relative to the link
            // caretRangeFromPoint is available in modern browsers
            const caretRangeFromPoint = property(
              document,
              "caretRangeFromPoint"
            );
            const range =
              typeof caretRangeFromPoint === "function"
                ? caretRangeFromPoint.call(document, e.clientX, e.clientY)
                : null;
            if (range instanceof Range) {
              // Set the caret at the clicked position
              const selection = window.getSelection();
              if (selection) {
                selection.removeAllRanges();
                selection.addRange(range);
                // Focus the parent element
                parent.focus();
              }
            }
          }
          return;
        }

        // Single click to open the link
        const href = el.getAttribute("href");
        if (!href) return;

        e.preventDefault();
        e.stopPropagation();

        const isInternal = href.startsWith("/");

        if (isInternal && router) {
          // Internal URL - navigate in same window
          await navigateToInternalPathOrObject(controller, router, href);
        } else {
          // External URL - open in new tab
          await openExternalUrl(href, controller, false, router ?? null);
        }
      };
      if (action === "add" && !el._tippy) {
        remove();
        el.addEventListener("mousedown", (e) => mousedown(controller, e));
        el.addEventListener("click", click);
        // Mark that we've set up listeners to avoid duplicates
        el._tippy = null;
      } else if (action === "remove") remove();
    }
  );
}

export async function getHierarchyTitle(
  parent: ParentId | null | undefined,
  controller: Controller
): Promise<ReleasableRef<string | null>> {
  if (!parent) return releasableRef(null);
  switch (parent.kind) {
    case "event": {
      const event = await controller.event.get(parent.id);
      return event.map((e) => e?.title || null);
    }
    case "task": {
      const task = await controller.task.get(parent.id);
      return task.map((task) => task?.title || null);
    }
    case "page": {
      const page = await controller.page.get(parent.id);
      return page.map(
        (page) => page?.title || (page?.title === "" ? "Untitled" : null)
      );
    }
    case "people": {
      const people = await controller.people.get(parent.id);
      return people.map(
        (people) => people?.name?.display || people?.email || null
      );
    }
    case "object": {
      const title = await useObjectTitle(controller, parent.id);
      return title.map((value) => value || "Untitled");
    }
    default:
      return releasableRef(null);
  }
}

export async function clickHierarchy(
  controller: Controller,
  router: Router,
  parent: ParentId,
  event?: MouseEvent | KeyboardEvent
): Promise<void> {
  switch (parent.kind) {
    case "page":
    case "event":
      navigateToObject(router, controller, parent satisfies ReferenceId, event);
      break;
    case "task": {
      const task = await controller.task.get(parent.id);
      task.release();
      if (task.value) {
        navigateToObject(
          router,
          controller,
          {
            kind: "task",
            id: task.value.id,
          } satisfies ReferenceId,
          event
        );
      }
      break;
    }
    case "people":
      {
        const people = await controller.people.get(parent.id);
        people.release();
        if (people.value) {
          navigateToObject(
            router,
            controller,
            {
              kind: "people",
              id: people.value.id,
            } satisfies ReferenceId,
            event
          );
        }
      }
      break;
    case "object":
      navigateToObject(router, controller, parent satisfies ReferenceId, event);
      break;
    default:
      break;
  }
}

export function isEllipsisActive(tag: HTMLElement | null): boolean {
  if (tag) return tag.offsetWidth < tag.scrollWidth;
  else return false;
}

export function exportableDocument(notes: Document | undefined): string {
  if (notes && notes.blocks.length > 0) {
    const exportableNotes = _.map(notes.blocks, (block, index) => {
      if (block.type === "paragraph") {
        return block.content;
      }
      if (block.type === "bullet") {
        if (block.list_type === "ordered") {
          const firstListElement = _.findLastIndex(
            notes.blocks,
            (block, currentIndex) =>
              currentIndex < index &&
              (block.type !== "bullet" || block.list_type !== "ordered")
          );
          return `${index - firstListElement}. ${block.content}`;
        }
        return `- ${block.content}`;
      }
      if (block.type === "blockquote") {
        return `> ${block.content}`;
      }
      if (block.type === "divider") {
        return `---`;
      }
      if (block.type === "embed") {
        return `${block.content}`;
      }
      if (block.type === "heading") {
        return `${"#".repeat(block.level)} ${block.content}`;
      }
      if (block.type === "todo") {
        return `- [${block.checked ? "x" : " "}] ${block.content}`;
      }
      if (block.type === "recurrent_task") {
        return `- ( ) ${block.content}`;
      }
    }).join("\n");
    return `\nNotes:\n${exportableNotes}\n\nExported from Routine (https://routine.co)`;
  }
  return "\n\nExported from Routine (https://routine.co)";
}
export const exportDate = (event: FullEvent) => {
  return `\nDate: ${format(event.start, "yyyy MMM d, h:mm aaa")} - ${format(
    event.end,
    "h:mm aaa"
  )}`;
};
export const exportLocation = (event: FullEvent) => {
  return event.location ? `\nLocation: ${event.location}` : "";
};
export const exportParticipant = (event: FullEvent) => {
  if (event.attendees && event.attendees.length > 0) {
    const organizer: Array<Attendee> = _.filter(
      event.attendees,
      (attendee) => attendee.organizer == true
    );
    const going = _.filter(
      event.attendees,
      (attendee) => attendee.organizer != true && attendee.status === "yes"
    );
    const maybe = _.filter(
      event.attendees,
      (attendee) => attendee.organizer != true && attendee.status === "maybe"
    );
    const notGoing = _.filter(
      event.attendees,
      (attendee) => attendee.organizer != true && attendee.status === "no"
    );
    const needsAction = _.filter(
      event.attendees,
      (attendee) => attendee.organizer != true && attendee.status === null
    );
    return `\nParticipants:\n- ${_.concat(
      organizer,
      going,
      maybe,
      notGoing,
      needsAction
    )
      .map(
        (attendee) =>
          `${attendee.name ? attendee.name + " " : ""}(${attendee.email})`
      )
      .join("\n- ")}`;
  }
  return "";
};
export const exportDescription = (event: FullEvent) => {
  return event.description ? `\nDescription:\n${event.description}` : "";
};
export const exportOtherEmails = (person: People) => {
  return person.otherEmails && person.otherEmails.length > 0
    ? `\nEmail Addresses:\n- ${_.join(person.otherEmails, "\n- ")}`
    : "";
};
export const exportPhones = (person: People) => {
  return person.phones && person.phones.length > 0
    ? `\nPhone Numbers:\n- ${_.join(person.phones, "\n- ")}`
    : "";
};
export function notifyCheckBrowser(controller: Controller) {
  controller.notifications.notify("Check your default browser to continue");
}

export const getHTMLElement = (
  el: HTMLElement | { $el: HTMLElement }
): HTMLElement => (el instanceof HTMLElement ? el : el.$el);

export { addOrRemoveListenerOnLink, findNextAllocation };
